/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {dlsColor, dlsFontWeight, dlsTextColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {TimeModes} from '@ion/dls/src/types/types';
import {
  LIMIT_ERROR_USAGE_BOX_GAP,
  LIMIT_ERROR_USAGE_BOX_PADDING,
  LIMIT_ERROR_USAGE_BOX_RADIUS,
  LIMIT_ERROR_USAGE_TEST_ID,
  LIMIT_WARNING_GAP,
  LIMIT_WARNING_ICON_GAP,
  LIMIT_WARNING_ICON_SIZE,
  LIMIT_WARNING_MIN_HEIGHT,
  LIMIT_WARNING_PADDING,
  LIMIT_WARNING_RADIUS,
  LIMIT_WARNING_TEST_ID
} from './constants';

// the two places the warning is set, each in its own design: the strip a
// drawer's footer carries over its actions, and the usage box the limit
// error card carries under its message ...
export enum TimeModeLimitWarningVariant {
  footer = 'footer',
  card = 'card'
}

// defines the prop definitions for the TimeModeLimitWarning component. The
// mode is only named by the footer's own ask - the card leaves the ask to the
// message above it ...
export type TimeModeLimitWarningProps = {
  mode?: TimeModes;
  assets: number;
  days: number;
  max: number;
  variant?: TimeModeLimitWarningVariant;
};

/**
 * Resolves one entry of the Time Mode limit copy, shared by the limit error
 * and the limit warning so the two read alike wherever either is shown.
 *
 * ```typescript
 * import {limitText} from '@ion/dls/src/maps/components/timeMode/current/TimeModeLimitWarning';
 *
 * ...
 *
 * const title = limitText('tooManyAssets.title');
 * ```
 *
 * @param key     - the entry under the limit error's copy
 * @param options - the values the entry interpolates
 * @returns The resolved copy.
 */
export const limitText = (key: string, options?: object): string => (
  translate(`maps:controls.timeModeControl.limitError.${key}`, options)
);

// the warm tint the warning is set on - the theme's `orange` (#E17200) at 5%
// opacity, so it reads off whatever it is drawn over. Resolved when the
// styles are, so it follows the active theme ...
const usageTint = (): string => `${dlsColor('orange')}0D`;

// defining style for the TimeModeLimitWarning component ...
const useStyles = makeStyles(() => ({
  // ---- shared by both variants ----
  icon : {
    color      : dlsTextColor('orange'),
    flexShrink : 0
  },
  // the figures take their face from the theme's typography - `body4` is
  // Archivo Narrow Medium at 12px/16px, `label1` Archivo Narrow Bold at 16px.
  // The theme states its tracking without a unit, which CSS would drop, so
  // each figure's tracking is restated in px, as is any line height the
  // design sets apart from the theme's ...
  //
  // "Currently: 10 assets × 30 day =" - `body4`, tracked out 0.5px and sat on
  // the line's bottom. The "Currently:" label reads in the same face as the
  // figures it opens ...
  current : {
    ...dlsTypography('body4'),
    color         : dlsColor('grey2'),
    fontStyle     : 'normal',
    lineHeight    : '16px',
    letterSpacing : '0.5px',
    verticalAlign : 'bottom'
  },
  // the total the first line closes on - "300" - Archivo Narrow Bold at 16px
  // on a 100% line height, tracked out 1px, uppercased and centred. At 100%
  // its line is the 16px the line around it already runs at, so the larger
  // figure does not push the line taller ...
  total : {
    ...dlsTypography('label1'),
    color         : dlsColor('grey2'),
    fontStyle     : 'normal',
    lineHeight    : '100%',
    letterSpacing : '1px',
    textTransform : 'uppercase',
    verticalAlign : 'middle'
  },
  // "Max: 120" - Archivo Narrow at 12px/16px, tracked out 0.5px and sat on
  // the line's bottom; its weight is each variant's own ...
  max : {
    ...dlsTypography('body4'),
    color         : dlsColor('grey2'),
    fontStyle     : 'normal',
    lineHeight    : '16px',
    letterSpacing : '0.5px',
    verticalAlign : 'bottom'
  },
  // ---- footer variant ----
  // the strip itself - the limit error's warm tint, the footer's full width,
  // 56px tall on an 8px inset and radius, and 8px between its two lines ...
  footer : {
    gap             : `${LIMIT_WARNING_GAP}px`,
    width           : '100%',
    display         : 'flex',
    padding         : `${LIMIT_WARNING_PADDING}px`,
    minHeight       : `${LIMIT_WARNING_MIN_HEIGHT}px`,
    boxSizing       : 'border-box',
    borderRadius    : `${LIMIT_WARNING_RADIUS}px`,
    flexDirection   : 'column',
    backgroundColor : usageTint()
  },
  // the first line - the warning icon and the ask, 4px apart, in the
  // design's Inter Medium at 12px/16px, untracked ...
  footerAsk : {
    ...dlsTypography('cta2'),
    gap           : `${LIMIT_WARNING_ICON_GAP}px`,
    color         : dlsColor('grey2'),
    display       : 'flex',
    fontStyle     : 'normal',
    alignItems    : 'center',
    letterSpacing : '0px'
  },
  footerIcon : {
    fontSize : `${LIMIT_WARNING_ICON_SIZE}px`
  },
  // the second line - the figures to the left and the limit to the right ...
  footerFigures : {
    display        : 'flex',
    alignItems     : 'baseline',
    justifyContent : 'space-between'
  },
  footerMax : {
    fontWeight : dlsFontWeight(500, 500)
  },
  // ---- card variant ----
  // the tinted box itself - 302x48, two 16px lines on its own 8px padding,
  // with 4px between the warning icon and the figures. `border-box` so the
  // padding is drawn inside the box rather than added to it ...
  card : {
    gap             : `${LIMIT_ERROR_USAGE_BOX_GAP}px`,
    display         : 'flex',
    padding         : `${LIMIT_ERROR_USAGE_BOX_PADDING}px`,
    boxSizing       : 'border-box',
    alignItems      : 'center',
    borderRadius    : `${LIMIT_ERROR_USAGE_BOX_RADIUS}px`,
    backgroundColor : usageTint()
  },
  cardIcon : {
    fontSize : '24px'
  },
  // the figures, one line over the other ...
  cardFigures : {
    display       : 'flex',
    flexDirection : 'column'
  },
  cardMax : {
    fontWeight : dlsFontWeight(700, 700)
  }
}));

/**
 * The TimeModeLimitWarning component states that a selection would take a
 * Timeline or History report over its limit - the assets × days it comes to
 * against the most allowed. It is set in either of two designs, each styled
 * apart from the other: the `footer` strip a drawer carries over its actions,
 * which also asks for fewer assets, and the `card` usage box the limit error
 * carries under its own message.
 *
 * ```typescript
 *
 *   import TimeModeLimitWarning from '@ion/dls/src/maps/components/timeMode/current/TimeModeLimitWarning';
 *   ...
 *
 *   // the strip a drawer's footer carries ...
 *   <TimeModeLimitWarning mode={TimeModes.history}
 *     assets={10}
 *     days={30}
 *     max={120} />
 *
 *   // the usage box the limit error card carries ...
 *   <TimeModeLimitWarning variant={TimeModeLimitWarningVariant.card}
 *     assets={281}
 *     days={1}
 *     max={120} />
 *
 * ```
 *
 * @param mode    - the time mode the report runs in, named by the footer's ask
 * @param assets  - the assets the selection comes to
 * @param days    - the days the report spans
 * @param max     - the limit, as assets × days, the report may run up to
 * @param variant - the design it is set in, `footer` unless named
 * @returns The limit warning, in the variant's own design.
 */
export const TimeModeLimitWarning = ({
  mode,
  assets,
  days,
  max,
  variant = TimeModeLimitWarningVariant.footer
}: TimeModeLimitWarningProps) => {
  const classes = useStyles();
  const isCard  = variant === TimeModeLimitWarningVariant.card;

  // the figures both variants carry, written once - each variant only lays
  // them out and weighs its limit ...
  const figures = (
    <>
      <span className={classes.current}>
        {limitText('usage.currently')}
        {' '}
        {limitText(days === 1 ? 'usage.day' : 'usage.days', {assets, count: days})}
        {' = '}
        <span className={classes.total}>
          {assets * days}
        </span>
      </span>
      <span className={classnames(classes.max, isCard ? classes.cardMax : classes.footerMax)}>
        {limitText('usage.max')}
        {' '}
        {max}
      </span>
    </>
  );

  return isCard ? (
    <div className={classes.card} data-testid={LIMIT_ERROR_USAGE_TEST_ID}>
      <ReportProblemOutlinedIcon className={classnames(classes.icon, classes.cardIcon)} />
      <div className={classes.cardFigures}>
        {figures}
      </div>
    </div>
  ) : (
    <div className={classes.footer} role='alert' data-testid={LIMIT_WARNING_TEST_ID}>
      <span className={classes.footerAsk}>
        <ReportProblemOutlinedIcon className={classnames(classes.icon, classes.footerIcon)} />
        {limitText('selectFewer', {mode: translate(`maps:controls.timeModeControl.${mode}`)})}
      </span>
      <div className={classes.footerFigures}>
        {figures}
      </div>
    </div>
  );
};

export default memo(TimeModeLimitWarning);
